/**
 * The hero's three images. A27 §1.
 *
 * A slow wipe on a clock, one image to the next, that stops the moment a hand
 * or a pointer is on it and never starts if the visitor has asked for less
 * motion. The dots are real buttons, so a person can choose an image instead
 * of waiting for it.
 *
 * The clock is the one interval on the site that is not a motion token: it is
 * a reading pace, not a transition. Its value comes from the section setting.
 */

const DEFAULT_INTERVAL_MS = 3000;

class HeroSlides extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.dots = [...this.querySelectorAll('[data-dot]')];
    if (this.slides.length < 2) return;

    this.at = 0;
    this.interval = Number(this.dataset.interval) || DEFAULT_INTERVAL_MS;
    this.addEventListener('click', (event) => {
      const dot = event.target.closest('[data-dot]');
      if (!dot) return;
      this.show(this.dots.indexOf(dot));
      this.stop();
    });

    this.addEventListener('pointerenter', () => this.stop());
    this.addEventListener('pointerleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stop();
      else this.start();
    });

    this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  show(index) {
    this.at = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, i) => slide.classList.toggle('is-current', i === this.at));
    this.dots.forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === this.at)));
  }

  start() {
    if (this.timer) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.timer = setInterval(() => this.show(this.at + 1), this.interval);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }
}

if (!customElements.get('hero-slides')) customElements.define('hero-slides', HeroSlides);
